'use client';

import { motion } from 'motion/react';
import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useBoolean, useInstructionFeedbackHelper, useTextMeasurement, useTextSegmentation, useTooltipManager } from '@/hooks/utils';
import { cn } from '@/lib/utils';
import type { SequenceInstructionFeedbackResult } from '@/services/httpServices/SequenceServices/types';
import type { GeneralFormProps } from '@/types/general';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '../ui/form';
import { Icon } from '../ui/icon';
import { Textarea } from '../ui/textarea';

// Stable reference to prevent infinite re-renders
const EMPTY_FEEDBACK_RESULTS: SequenceInstructionFeedbackResult[] = [];

// Animation variants - extracted to minimize code within component
const ANIMATION_VARIANTS = {
  marker: {
    initial: { opacity: 0, scaleX: 0.95, y: 2 },
    animate: {
      opacity: 1,
      scaleX: 1,
      y: 0,
      transition: { duration: 0.25, ease: 'easeOut' as const },
    },
    exit: { opacity: 0, transition: { duration: 0.15 } },
  },
  tooltip: {
    initial: { opacity: 0, scale: 0.9, y: 10 },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { type: 'spring' as const, stiffness: 300, damping: 20 },
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      y: 5,
      transition: { duration: 0.15 },
    },
  },
} as const;

type Props = {
  placeholder?: string;
  rows?: number;
  feedbackResults?: SequenceInstructionFeedbackResult[];
  isLoading?: boolean;
  isFeedbackValid?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  onFeedbackClick?: (feedback: SequenceInstructionFeedbackResult) => void;
  /** Extra classes merged onto the underlying textarea element. */
  className?: string;
  /**
   * Canonical helper text shown below the field.
   * Falls back to `description` for backward compatibility.
   */
  helperText?: string;
} & GeneralFormProps;

// Separate component for tooltip to keep the main component cleaner
function GrammarFeedbackTooltip({
  activeFeedback,
  position,
  severityColorMap,
  severityIconMap,
}: {
  activeFeedback: SequenceInstructionFeedbackResult | null;
  position: { x: number; y: number };
  severityColorMap: Record<string, string>;
  severityIconMap: Record<string, string>;
}) {
  if (!activeFeedback)
    return null;

  // Get the severity for the icon and color
  const severity = activeFeedback.severity || 'MEDIUM';
  const iconName = severityIconMap[severity] || '';
  const colorClass = severityColorMap[severity] || '';

  return (
    <motion.div
      initial="initial"
      animate="animate"
      exit="exit"
      variants={ANIMATION_VARIANTS.tooltip}
      className={cn(
        'absolute z-[var(--z-modal)] rounded-md border bg-popover p-3 type-body-m text-popover-foreground shadow-lg',
        'w-[280px] max-w-[280px] pointer-events-none',
      )}
      style={{
        top: `${position.y + 20}px`,
        left: `${position.x + 5}px`,
        filter: 'drop-shadow(0 4px 3px rgb(0 0 0 / 0.07)) drop-shadow(0 2px 2px rgb(0 0 0 / 0.06))',
      }}
    >
      {/* Arrow pointing to text */}
      <div
        className="absolute w-3 h-3 bg-popover rotate-45 border"
        style={{
          top: '-6px',
          left: '14px',
          borderRight: 'none',
          borderBottom: 'none',
        }}
      />
      <div className="flex flex-col gap-2">
        <div className="flex items-center">
          <Icon
            icon={iconName}
            className={cn('mr-1.5', colorClass)}
            size="xs"
          />
          <span className={cn('type-body-s font-semibold', colorClass)}>
            {activeFeedback.title || ''}
          </span>
        </div>
        {activeFeedback.highlightText && (
          <div className="mt-1 pt-1 border-t border-border">
            <p className="type-body-s italic">
              "
              {activeFeedback.highlightText}
              "
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Feedback marker component to reduce complexity in the main component
function GrammarFeedbackMarker({
  feedback,
  displayValue,
  getTextSegments,
  textareaMetrics,
  onFeedbackHover,
  onFeedbackMove,
  onFeedbackSelect,
  onFeedbackClick,
  bgColor,
  textColor,
}: {
  feedback: SequenceInstructionFeedbackResult;
  displayValue: string;
  getTextSegments: (
    text: string,
    startIndex: number,
    width: number,
    paddingLeft: number,
    lineHeight: number,
    paddingTop: number,
  ) => Array<{ x: number; y: number; width: number }>;
  textareaMetrics: { paddingTop: number; paddingLeft: number; lineHeight: number; textareaWidth: number };
  onFeedbackHover: (e: React.MouseEvent, feedback: SequenceInstructionFeedbackResult) => void;
  onFeedbackMove: (e: React.MouseEvent, feedback: SequenceInstructionFeedbackResult) => void;
  onFeedbackSelect: (start: number, end: number) => void;
  onFeedbackClick?: (feedback: SequenceInstructionFeedbackResult) => void;
  bgColor: string;
  textColor: string;
}) {
  const translate = useTranslations();

  if (typeof feedback.highlightBegin !== 'number' || typeof feedback.highlightEnd !== 'number') {
    return null;
  }

  // Only process markers that are within the text length
  const startIndex = Math.min(feedback.highlightBegin, displayValue.length);
  const endIndex = Math.min(feedback.highlightEnd, displayValue.length);

  if (startIndex === endIndex) {
    return null;
  }

  // Get the highlighted text
  const highlightedText = displayValue.substring(startIndex, endIndex);

  // Get accurate text segments with proper positioning
  const { paddingTop, paddingLeft, lineHeight, textareaWidth } = textareaMetrics;
  const segments = getTextSegments(
    highlightedText,
    startIndex,
    textareaWidth,
    paddingLeft,
    lineHeight,
    paddingTop,
  );

  if (segments.length === 0) {
    return null;
  }

  // Fine-tune the underline position for visual alignment
  const underlineOffset = 1.5;

  return (
    <>
      {segments.map((segment, segmentIndex) => (
        <div key={`segment-${segmentIndex}`} className="relative">
          {/* Hover detection area for this segment */}
          <div
            role="button"
            tabIndex={0}
            className="absolute hover:bg-foreground/5 transition-colors duration-[var(--duration-short-3)] ease-[var(--ease-emphasized)] motion-reduce:transition-none"
            style={{
              left: `${segment.x}px`,
              top: `${segment.y}px`,
              height: `${lineHeight}px`,
              width: `${segment.width}px`,
              cursor: 'pointer',
              zIndex: 20,
              pointerEvents: 'auto',
            }}
            onMouseEnter={e => onFeedbackHover(e, feedback)}
            onMouseMove={e => onFeedbackMove(e, feedback)}
            onClick={() => {
              onFeedbackSelect(startIndex, endIndex);
              onFeedbackClick?.(feedback);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onFeedbackSelect(startIndex, endIndex);
                onFeedbackClick?.(feedback);
              }
            }}
            aria-label={translate('sequence.grammar.suggestionAria', { title: feedback.title ?? '' })}
          />

          {/* Background highlight for this segment */}
          <motion.div
            initial="initial"
            animate="animate"
            exit="exit"
            variants={ANIMATION_VARIANTS.marker}
            className={cn('absolute rounded-sm shadow-sm', bgColor)}
            style={{
              left: `${segment.x}px`,
              top: `${segment.y}px`,
              height: `${lineHeight}px`,
              width: `${segment.width}px`,
              zIndex: 10,
              pointerEvents: 'none',
              transformOrigin: 'left',
            }}
          />

          {/* Underline for this segment - with pixel-perfect positioning */}
          <motion.div
            initial="initial"
            animate="animate"
            exit="exit"
            variants={ANIMATION_VARIANTS.marker}
            className={cn('absolute shadow-sm bg-current', textColor)}
            style={{
              left: `${segment.x}px`,
              top: `${segment.y + lineHeight - underlineOffset}px`,
              height: '2px',
              width: `${segment.width}px`,
              zIndex: 15,
              pointerEvents: 'none',
              transformOrigin: 'left',
            }}
          />
        </div>
      ))}
    </>
  );
}

function RHFGrammarTextarea({
  name,
  title,
  description,
  helperText,
  placeholder,
  required,
  value: externalValue,
  onChange: externalOnChange,
  rows,
  feedbackResults: feedbackResultsProp = EMPTY_FEEDBACK_RESULTS,
  isLoading = false,
  isFeedbackValid = true,
  onKeyDown,
  onPaste,
  onFeedbackClick,
  className,
  ...props
}: Props) {
  const { control } = useFormContext<Record<string, string>>();
  const resolvedHelperText = helperText ?? description;
  const { severityColorMap, severityBgColorMap, severityIconMap } = useInstructionFeedbackHelper();

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // State
  const { value: isFocused, setValue: setIsFocused } = useBoolean(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [textareaSize, setTextareaSize] = useState({ width: 0, height: 0 });

  // Custom hooks
  const { measureText, setupResizeObserver } = useTextMeasurement();
  const tooltipManager = useTooltipManager<SequenceInstructionFeedbackResult>();

  // Watch form field value
  const value = useWatch({ control, name });

  // Determine the final value to display
  const displayValue = useMemo(() => value ?? externalValue ?? '', [value, externalValue]);

  // Text segmentation hook - we only need getTextSegments for this component
  const { getTextSegments } = useTextSegmentation({
    measureText,
    displayValue,
  });

  // Update text measurements when textarea changes size
  const updateTextAreaMetrics = useCallback(() => {
    if (textareaRef.current !== null) {
      const { width, height } = textareaRef.current.getBoundingClientRect();

      // Only update if size actually changed
      setTextareaSize((prev) => {
        if (width !== prev.width || height !== prev.height) {
          return { width, height };
        }
        return prev;
      });
    }
  }, [textareaSize]);

  // Handle textarea resize
  useEffect(() => {
    if (!textareaRef.current)
      return undefined;

    // Initial size calculation
    updateTextAreaMetrics();

    // Set up ResizeObserver using our custom hook
    const cleanup = setupResizeObserver(textareaRef.current, updateTextAreaMetrics);

    // Also listen for window resize events
    window.addEventListener('resize', updateTextAreaMetrics);

    // Cleanup
    return () => {
      cleanup();
      window.removeEventListener('resize', updateTextAreaMetrics);
    };
  }, [updateTextAreaMetrics, setupResizeObserver]);

  // Track scroll position of textarea
  const handleScroll = useCallback(() => {
    if (textareaRef.current) {
      setScrollPosition((prev) => {
        const newScrollTop = textareaRef.current?.scrollTop || 0;
        return newScrollTop !== prev ? newScrollTop : prev;
      });
    }
  }, []);

  useEffect(() => {
    if (textareaRef.current === null)
      return undefined;

    handleScroll(); // Initial scroll position
    textareaRef.current.addEventListener('scroll', handleScroll);

    return () => {
      if (textareaRef.current) {
        textareaRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  // Filter active feedback results
  const activeResults = useMemo(() => {
    if (!feedbackResultsProp?.length || isFeedbackValid === false) {
      return [];
    }
    return feedbackResultsProp;
  }, [feedbackResultsProp, isFeedbackValid]);

  // Handle focus and blur
  const handleFocus = () => {
    setIsFocused(true);
    tooltipManager.clearActiveFeedback();
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  // Function to focus and set cursor position
  const focusAndSelectText = useCallback((start: number, end: number) => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(start, end);
    }
  }, []);

  // Handle feedback hover and move (using our tooltip manager hook)
  const handleFeedbackHover = useCallback((e: React.MouseEvent, feedback: SequenceInstructionFeedbackResult) => {
    if (containerRef.current) {
      tooltipManager.handleFeedbackHover(e, feedback, containerRef, isFocused);
    }
  }, [tooltipManager, isFocused]);

  const handleFeedbackMove = useCallback((e: React.MouseEvent, feedback: SequenceInstructionFeedbackResult) => {
    if (containerRef.current) {
      tooltipManager.handleFeedbackMove(e, feedback, containerRef, isFocused);
    }
  }, [tooltipManager, isFocused]);

  // Calculate the metrics for marker rendering
  const getTextareaMetrics = useCallback(() => {
    if (!textareaRef.current) {
      return { paddingTop: 0, paddingLeft: 0, lineHeight: 0, textareaWidth: 0 };
    }

    const styles = window.getComputedStyle(textareaRef.current);
    return {
      paddingTop: Number.parseFloat(styles.paddingTop),
      paddingLeft: Number.parseFloat(styles.paddingLeft),
      lineHeight: Number.parseFloat(styles.lineHeight),
      textareaWidth: textareaRef.current.clientWidth,
    };
  }, []);

  // Simplified marker rendering with improved performance
  const renderMarkers = useCallback(() => {
    if (
      isFocused === true
      || activeResults.length === 0
      || !displayValue
      || !textareaRef.current
    ) {
      return null;
    }

    const textareaMetrics = getTextareaMetrics();

    return (
      <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden select-none">
        <div
          className="absolute w-full"
          style={{ transform: `translateY(-${scrollPosition}px)` }}
        >
          {activeResults.map((feedback: SequenceInstructionFeedbackResult, index: number) => (
            <GrammarFeedbackMarker
              key={`feedback-${index}`}
              feedback={feedback}
              displayValue={displayValue}
              getTextSegments={getTextSegments}
              textareaMetrics={textareaMetrics}
              onFeedbackHover={handleFeedbackHover}
              onFeedbackMove={handleFeedbackMove}
              onFeedbackSelect={focusAndSelectText}
              onFeedbackClick={onFeedbackClick}
              bgColor={severityBgColorMap[feedback.severity as keyof typeof severityBgColorMap] || severityBgColorMap.MEDIUM}
              textColor={severityColorMap[feedback.severity as keyof typeof severityColorMap] || severityColorMap.MEDIUM}
            />
          ))}
        </div>
      </div>
    );
  }, [
    isFocused,
    activeResults,
    displayValue,
    scrollPosition,
    getTextSegments,
    getTextareaMetrics,
    focusAndSelectText,
    handleFeedbackHover,
    handleFeedbackMove,
    severityBgColorMap,
    severityColorMap,
    onFeedbackClick,
  ]);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const { onChange, onBlur, name: fieldName, ref: fieldRef } = field;
        const { defaultValue, ...restProps } = props;

        const hasTitle = title !== undefined && title !== '';
        const floatingLabel: string | undefined = (() => {
          if (hasTitle === false) {
            return undefined;
          }
          if (required === true) {
            return `${title} *`;
          }
          return title;
        })();

        return (
          <FormItem className="w-full">
            <FormControl>
              <div
                ref={containerRef}
                className="relative"
                onMouseLeave={tooltipManager.clearActiveFeedback}
              >
                {/* The actual textarea for editing */}
                <Textarea
                  data-gramm="false"
                  data-gramm_editor="false"
                  data-enable-grammarly="false"
                  name={fieldName}
                  ref={(el) => {
                    if (el !== null) {
                      textareaRef.current = el;
                      if (typeof fieldRef === 'function') {
                        fieldRef(el);
                      } else if (fieldRef !== null) {
                        (fieldRef as React.MutableRefObject<HTMLTextAreaElement>).current = el;
                      }
                    }
                  }}
                  onFocus={handleFocus}
                  onBlur={() => {
                    handleBlur();
                    onBlur();
                  }}
                  rows={rows}
                  required={required === true}
                  variant={fieldState.error !== undefined ? 'error' : 'default'}
                  data-testid={fieldName}
                  label={floatingLabel}
                  placeholder={placeholder}
                  className={cn('resize-none w-full relative', activeResults.length > 0 && 'grammar-text', className)}
                  onChange={(e) => {
                    if (externalOnChange !== undefined) {
                      externalOnChange(e);
                    } else {
                      onChange(e);
                    }
                    updateTextAreaMetrics();
                  }}
                  onKeyDown={onKeyDown}
                  onPaste={onPaste}
                  value={displayValue}
                  disabled={isLoading}
                  {...restProps}
                />

                {/* Grammar markers layer */}
                {renderMarkers()}

                {/* Tooltip */}
                <GrammarFeedbackTooltip
                  activeFeedback={tooltipManager.activeFeedback}
                  position={tooltipManager.position}
                  severityColorMap={severityColorMap}
                  severityIconMap={severityIconMap}
                />
              </div>
            </FormControl>
            {resolvedHelperText !== undefined && resolvedHelperText !== '' && fieldState.error === undefined && (
              <FormDescription className="mt-1.5 type-body-s text-on-surface-variant">
                {resolvedHelperText}
              </FormDescription>
            )}
            <FormMessage className="mt-1.5 type-body-s text-error font-medium" />
          </FormItem>
        );
      }}
    />
  );
}

RHFGrammarTextarea.displayName = 'RHFGrammarTextarea';

export default RHFGrammarTextarea;
